/**
 * Track every live camera/mic MediaStreamTrack acquired during a call.
 * OpenTok destroy() often leaves tracks live → Chrome keeps the red
 * "recording" indicator on the tab after Leave.
 */

const liveTracks = new Set();
const liveStreams = new Set();

function addTrack(track) {
  if (!track || typeof track.stop !== "function") return;
  liveTracks.add(track);
  try {
    track.addEventListener?.(
      "ended",
      () => {
        liveTracks.delete(track);
      },
      { once: true },
    );
  } catch (_) {
    /* ignore */
  }
}

export function registerMediaStream(stream) {
  if (!stream) return;
  try {
    if (typeof stream.getTracks === "function") {
      liveStreams.add(stream);
      stream.getTracks().forEach(addTrack);
    }
  } catch (_) {
    /* ignore */
  }
}

export function registerMediaTrack(track) {
  addTrack(track);
}

export function registerFromPublisher(publisher) {
  if (!publisher) return;
  try {
    if (typeof publisher.getMediaStream === "function") {
      registerMediaStream(publisher.getMediaStream());
    }
  } catch (_) {
    /* ignore */
  }
  try {
    // OT internal shapes (varies by version)
    const internal = publisher._ || publisher;
    if (internal?.webRtcStream) registerMediaStream(internal.webRtcStream);
    if (internal?.localMediaStream) registerMediaStream(internal.localMediaStream);
    if (internal?.mediaStream) registerMediaStream(internal.mediaStream);
  } catch (_) {
    /* ignore */
  }
  try {
    const el = publisher.element || document.getElementById("publisher");
    if (el) {
      el.querySelectorAll?.("video, audio").forEach((node) => {
        if (node.srcObject) registerMediaStream(node.srcObject);
      });
      if (el.srcObject) registerMediaStream(el.srcObject);
    }
  } catch (_) {
    /* ignore */
  }
}

function stopTrack(track) {
  try {
    if (track && track.readyState !== "ended") track.stop();
  } catch (_) {
    /* ignore */
  }
  liveTracks.delete(track);
}

/**
 * Stop every registered track + DOM media elements that still hold live tracks.
 * Safe to call multiple times.
 */
export function stopAllRegisteredMedia() {
  try {
    liveTracks.forEach(stopTrack);
  } catch (_) {
    /* ignore */
  }
  liveTracks.clear();

  try {
    liveStreams.forEach((stream) => {
      try {
        stream.getTracks?.().forEach(stopTrack);
      } catch (_) {
        /* ignore */
      }
    });
  } catch (_) {
    /* ignore */
  }
  liveStreams.clear();

  try {
    if (typeof window !== "undefined" && window.localStream) {
      window.localStream.getTracks?.().forEach(stopTrack);
      window.localStream = null;
    }
  } catch (_) {
    /* ignore */
  }

  if (typeof document === "undefined") return;

  try {
    document.querySelectorAll("video, audio").forEach((el) => {
      try {
        const s = el.srcObject;
        if (s && typeof s.getTracks === "function") {
          s.getTracks().forEach(stopTrack);
          el.srcObject = null;
        }
      } catch (_) {
        /* ignore */
      }
      try {
        el.pause?.();
      } catch (_) {
        /* ignore */
      }
    });
  } catch (_) {
    /* ignore */
  }

  // OpenTok DOM leftovers
  try {
    document.querySelectorAll(".OT_root, .OT_publisher, .OT_subscriber, #publisher, #subscriber").forEach((root) => {
      try {
        root.querySelectorAll?.("video, audio").forEach((el) => {
          try {
            if (el.srcObject) {
              el.srcObject.getTracks?.().forEach(stopTrack);
              el.srcObject = null;
            }
          } catch (_) {
            /* ignore */
          }
        });
      } catch (_) {
        /* ignore */
      }
    });
  } catch (_) {
    /* ignore */
  }
}

export default {
  registerMediaStream,
  registerMediaTrack,
  registerFromPublisher,
  stopAllRegisteredMedia,
};
